import { useEffect } from 'react';
import { rankWith, ControlProps, isControl } from '@jsonforms/core';
import { withJsonFormsControlProps, useJsonForms } from '@jsonforms/react';
import { Autocomplete, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useDimensionDefinitions } from '@/hooks/finance/useDimensionDefinition';
import { DimensionDefinition } from '@/types/finance/DimensionDefinition';

/**
 * The PlanViewRowDimension `dimensionDefinitionId` picker. Two jobs:
 *
 * 1. Gate on the `rowSourceKind` discriminator: a DIMENSION axis references a dimension, an ACCOUNT axis
 *    does not (its rows are GL accounts). When the axis is not DIMENSION the control is hidden AND its
 *    value is cleared, so a dimension picked before a switch to ACCOUNT can't linger hidden and trip the
 *    server-side XOR guard (EnsureValidRowSources).
 * 2. Offer only ACTIVE dimensions as a new row axis. Deactivating a DimensionDefinition does NOT delete
 *    it or its DimensionValues, so an inactive dimension left on a view would still enumerate rows (and
 *    its stale value ids would fail on save). A selection that has since been deactivated is still shown
 *    — flagged "(inactive)" — so the misconfiguration is visible and fixable, but it is never offered to
 *    other rows. (The DimensionDefinition contract already states "Inactive definitions are not offered
 *    for new entry".)
 *
 * A bespoke picker (not the generic entity-lookup) because the lookup DTO/endpoint carry no `isActive`
 * and are generated; the dimension list is small, so a client-side autocomplete is simpler and correct.
 */
function scopeProperty(uischema: any): string | undefined {
  const scope: string | undefined = uischema?.scope;
  return scope ? scope.split('/').pop() : undefined;
}

export const RowSourceDimensionFieldComponent = (props: ControlProps) => {
  const { path, data, handleChange, enabled, visible, label, description, required, errors } = props;
  const { t } = useTranslation();
  const ctx = useJsonForms();
  const rowSourceKind = (ctx.core?.data as any)?.rowSourceKind as string | undefined;
  const isDimension = rowSourceKind === 'DIMENSION';

  const { data: definitions } = useDimensionDefinitions();

  // Clear a leftover dimension when the axis is not DIMENSION (e.g. switched to ACCOUNT), so the hidden
  // value never survives to save. Runs while mounted — hence this renderer owns hiding rather than a rule.
  useEffect(() => {
    if (!isDimension && data != null && data !== '') {
      handleChange(path, undefined);
    }
  }, [isDimension, data, path, handleChange]);

  if (!isDimension || visible === false) {
    return null;
  }

  const all = definitions ?? [];
  const active = all.filter(d => d.isActive);
  const selected = all.find(d => d.id === data) ?? null;
  // Keep an already-selected-but-now-inactive dimension visible so the row can be fixed; don't offer it
  // to other rows.
  const options = selected && !selected.isActive ? [selected, ...active] : active;
  const optionLabel = (d: DimensionDefinition) =>
    d.isActive ? d.name : `${d.name} (${t('common.inactive', { defaultValue: 'inactive' }).toLowerCase()})`;

  return (
    <Autocomplete
      options={options}
      value={selected}
      getOptionLabel={optionLabel}
      isOptionEqualToValue={(o, v) => o.id === v.id}
      disabled={!enabled}
      onChange={(_e, v) => handleChange(path, v ? v.id : undefined)}
      renderOption={(liProps, d) => (
        <li {...liProps} key={d.id}>{optionLabel(d)}</li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          error={!!errors}
          helperText={errors || description}
          variant="standard"
          size="small"
        />
      )}
      size="small"
    />
  );
};

export const RowSourceDimensionFieldRenderer = withJsonFormsControlProps(RowSourceDimensionFieldComponent);

/**
 * Tester — rank above the lookup renderer, but only for the `dimensionDefinitionId` control of the
 * PlanViewRowDimension form. The rootSchema guard (`rowSourceKind` is the `RowSourceKind` enum) keeps
 * this from matching another entity's `dimensionDefinitionId` lookup (e.g. AccountDimensionRule).
 */
export const rowSourceDimensionFieldTester = rankWith(25, (uischema, _schema, context) => {
  if (!isControl(uischema)) return false;
  if (scopeProperty(uischema) !== 'dimensionDefinitionId') return false;
  const rootProps = (context?.rootSchema as any)?.properties;
  return rootProps?.rowSourceKind?.enumType === 'RowSourceKind';
});

export const rowSourceDimensionFieldEntry = {
  tester: rowSourceDimensionFieldTester,
  renderer: RowSourceDimensionFieldRenderer,
};
